<template>
    <Layout>
        <template #form>
            <Form @submit="onSubmit" :show-feedback="showFeedback" show-go-to-login>
                <template #title>
                    {{ $tc('title', pluralization) }}
                </template>
                <template #subtitle>
                    {{ $tc('subtitle', pluralization) }}
                </template>
                <template #feedback>
                    <feedback-auth something-wrong />
                </template>
                <template #fields>
                    <InputPassword
                        v-model="newPassword"
                        :state="validateField('newPassword')"
                        :invalid-feedback="newPasswordInvalidFeedback"
                        new-password
                        autofocus
                    />
                    <InputPassword
                        v-model="confirmNewPassword"
                        :state="validateField('confirmNewPassword')"
                        :invalid-feedback="confirmNewPasswordInvalidFeedback"
                        confirm-new-password
                    />
                </template>
                <template #button>
                    <BtnSubmit save-password :loading="loading" :disabled="disabledSubmitButton" />
                </template>
            </Form>
        </template>
    </Layout>
</template>

<script>
import api from '@/services/api'
import oauth from '@/services/oauth-api'
import AuthService from '@/services/auth'
import { validationMixin } from '@/mixins'
import { required, sameAs, minLength, maxLength } from 'vuelidate/lib/validators'
import { alphaNumeric, upperAndLowerCases, specialCharacters } from '@/lib/validators'
import { Layout, Form, InputPassword, BtnSubmit, FeedbackAuth } from './components'
import { alertModalMixin } from './mixins'

/*
    Important:
    If you change these values, please, don't forget to change the translations inside the <InputPassword /> component.
 */
const minPasswordLength = 12
const maxPasswordLength = 64
// END

const initialState = {
    newPassword: null,
    confirmNewPassword: null,
}

export default {
    name: 'CreatePassword',
    mixins: [validationMixin, alertModalMixin],
    components: {
        FeedbackAuth,
        Layout,
        Form,
        InputPassword,
        BtnSubmit,
    },
    props: {
        expirePassword: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            token: '',
            email: '',
            newPassword: initialState.newPassword,
            confirmNewPassword: initialState.confirmNewPassword,
            disabledSubmitButton: false,
            loading: false,
            showFeedback: false,
        }
    },
    async created() {
        if (!this.$route.params.token) return this.toLogin()

        this.token = this.$route.params.token
        try {
            const resp = await AuthService(api, oauth).resetPasswordFind(this.token)
            const { email, success } = resp.data.data
            if (!success) {
                this.modalWarning(this.$t('resetPasswordLinkExpired', { login: '/v2/login/forgot-password' }))
                this.disabledSubmitButton = true
            }
            this.email = email
        } catch (e) {
            this.disabledSubmitButton = true
        }
    },
    validations: {
        newPassword: {
            required,
            minLength: minLength(minPasswordLength),
            maxLength: maxLength(maxPasswordLength),
            alphaNumeric,
            upperAndLowerCases,
            specialCharacters,
        },
        confirmNewPassword: {
            required,
            minLength: minLength(minPasswordLength),
            maxLength: maxLength(maxPasswordLength),
            alphaNumeric,
            upperAndLowerCases,
            specialCharacters,
            sameAs: sameAs('newPassword'),
        },
    },
    computed: {
        pluralization() {
            if (this.expirePassword) return 2
            return 1
        },
        newPasswordInvalidFeedback() {
            if (!this.$v.newPassword.required) return this.$t('required')
            if (!this.$v.newPassword.minLength) return this.$t('minLength', { min: minPasswordLength })
            if (!this.$v.newPassword.maxLength) return this.$t('maxLength', { max: maxPasswordLength })
            if (!this.$v.newPassword.alphaNumeric) return this.$t('alphaNumeric')
            if (!this.$v.newPassword.upperAndLowerCases) return this.$t('upperAndLowerCases')
            if (!this.$v.newPassword.specialCharacters) return this.$t('specialCharacters')
        },
        confirmNewPasswordInvalidFeedback() {
            if (!this.$v.confirmNewPassword.required) return this.$t('required')
            if (!this.$v.confirmNewPassword.minLength) return this.$t('minLength', { min: minPasswordLength })
            if (!this.$v.confirmNewPassword.maxLength) return this.$t('maxLength', { max: maxPasswordLength })
            if (!this.$v.confirmNewPassword.alphaNumeric) return this.$t('alphaNumeric')
            if (!this.$v.confirmNewPassword.upperAndLowerCases) return this.$t('upperAndLowerCases')
            if (!this.$v.confirmNewPassword.specialCharacters) return this.$t('specialCharacters')
            if (!this.$v.confirmNewPassword.sameAs) return this.$t('sameAs')
        },
    },
    methods: {
        async onSubmit() {
            if (this.validate()) {
                try {
                    this.loading = true
                    this.showFeedback = false
                    await AuthService(api, oauth).resetPassword(this.token, this.email, this.newPassword)
                    this.modalSuccess(this.$t('passwordReset'), this.toLogin)
                } catch (e) {
                    this.showFeedback = true
                } finally {
                    this.reset()
                    this.loading = false
                }
            }
        },
        reset() {
            this.newPassword = initialState.newPassword
            this.confirmNewPassword = initialState.confirmNewPassword
            this.$v.$reset()
        },
        toLogin() {
            return this.$router.push({ name: 'Login' })
        },
    },
    i18n: {
        messages: {
            'en-US': {
                title: 'Reset your password | Your password has expired',
                subtitle:
                    'Here you can create the password for your first access to the app or reset your current password' +
                    ' | Create a new password, it will be valid for 1 year.',
                passwordReset: 'Password reset successfully!',
                required: 'Required field',
                minLength: 'Your password has fewer than {min} characters',
                maxLength: 'Your password has more than {max} characters',
                alphaNumeric: 'Your password must be alphanumeric',
                upperAndLowerCases: 'Your password must contain both uppercase and lowercase letters',
                specialCharacters: 'Your password must contain at least 1 special character',
                sameAs: 'Passwords do not match',
                resetPasswordLinkExpired: 'Expired password reset link. To generate a new request',
            },
            'es-ES': {
                title: 'Restablezca su contraseña | Tu contraseña ha expirado',
                subtitle:
                    'Aquí puedes crear la contraseña para tu primer acceso a la app o restablecer tu contraseña actual' +
                    ' | Crea una nueva contraseña, será válida por 1 año',
                passwordReset: '¡Contraseña restablecida con éxito!',
                required: 'Campo obligatorio',
                minLength: 'Tu contraseña tiene menos de {min} caracteres',
                maxLength: 'Tu contraseña tiene más de {max} caracteres',
                alphaNumeric: 'Tu contraseña debe ser alfanumérica',
                upperAndLowerCases: 'Tu contraseña debe contener letras mayúsculas y minúsculas',
                specialCharacters: 'Tu contraseña debe contener al menos 1 carácter especial',
                sameAs: 'Las contraseñas no coinciden',
                resetPasswordLinkExpired:
                    'Enlace de restablecimiento de contraseña caducado. Para generar una nueva solicitud <a href="{login}">haz clic aquí</a>.',
            },
            'pt-BR': {
                title: 'Crie sua nova senha | Sua senha expirou',
                subtitle:
                    'Aqui você pode criar a senha para o seu primeiro acesso ao app ou redefinir a sua senha atual ' +
                    ' | Crie uma nova senha, ela será valida por 1 ano.',
                passwordReset: 'Senha redefinida com sucesso!',
                required: 'Preenchimento obrigatório',
                minLength: 'Sua senha tem menos de {min} caracteres',
                maxLength: 'Sua senha tem mais de {max} caracteres',
                alphaNumeric: 'Sua senha deve ser alfanumérica',
                upperAndLowerCases: 'Sua senha deve conter letras maiúsculas e minúsculas',
                specialCharacters: 'Sua senha deve conter pelo menos 1 caractere especial',
                sameAs: 'As senhas não conferem',
                resetPasswordLinkExpired:
                    'Link de redefinição de senha expirado. Para gerar uma nova solicitação <a href="{login}">clique aqui</a>.',
            },
        },
    },
}
</script>
